import { CenteredEmptyState } from '@components/display/emptyStates';
import { Box } from '@mui/material';
import { NotificationItemCard } from '@notifications/components/display/cards';
import { useNotificationsBoxContext } from '@notifications/contexts';
import { useLazyGetPaginatedNotificationsQuery } from '@notifications/services';
import { forwardRef, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { List } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

const PAGE_SIZE = 6;

export const ReadNotificationsBox = forwardRef((props, ref) => {
    const { readNotificationsState: [notifications, setNotifications] = [[], () => {}] } = useNotificationsBoxContext();
    //#region Infinite Loader State
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isNextPageLoading, setIsNextPageLoading] = useState(false);
    const [page, setPage] = useState<number>(1);
    //#endregion

    //#region Infinite Loader queries
    const [loadMoreNotifications, { isFetching: isFetchingMoreNotifications }] =
        useLazyGetPaginatedNotificationsQuery();
    //#endregion

    //#region Infinite Loader variables
    const rowCount = hasNextPage ? notifications.length + 1 : notifications.length;
    //#endregion

    //#region Infinite Loader functions
    const isRowLoaded = useCallback(
        (index: number) => {
            return !hasNextPage || index < notifications.length;
        },
        [hasNextPage, notifications]
    );

    const handleLoadMoreNotifications = useCallback(async () => {
        if (isNextPageLoading || !hasNextPage) {
            return;
        }

        setIsNextPageLoading(true);

        try {
            const response = await loadMoreNotifications({
                is_read: true,
                page,
                page_size: PAGE_SIZE,
            }).unwrap();
            if (response && response.results.length > 0) {
                setNotifications((prevNotifications) => {
                    const existingIds = new Set(prevNotifications.map((notification) => notification.id));
                    const uniquePageResults = response.results.filter((notification) => {
                        if (existingIds.has(notification.id)) {
                            return false;
                        }

                        existingIds.add(notification.id);
                        return true;
                    });

                    return [...prevNotifications, ...uniquePageResults];
                });
                setPage((prevPage) => prevPage + 1);
                setHasNextPage(response.results.length === PAGE_SIZE);
            } else {
                setHasNextPage(false);
            }
        } catch (error) {
            console.error('Error loading more notifications:', error);
            setHasNextPage(false);
        } finally {
            setIsNextPageLoading(false);
        }
    }, [isNextPageLoading, hasNextPage, page]);

    const onRowsRendered = useInfiniteLoader({
        isRowLoaded,
        loadMoreRows: handleLoadMoreNotifications,
        rowCount,
        minimumBatchSize: PAGE_SIZE,
        threshold: 1,
    });
    //#endregion

    return (
        <Box height={{ xs: '100%', md: '400px' }} width="100%" ref={ref} {...props}>
            <List
                onRowsRendered={onRowsRendered}
                rowComponent={NotificationItemCard}
                rowCount={rowCount}
                rowHeight={67}
                rowProps={{ notifications, hasNextPage }}
                style={notifications.length !== 0 && !isFetchingMoreNotifications ? { height: '100%' } : undefined}
            />
            {notifications.length === 0 && !isFetchingMoreNotifications && (
                <CenteredEmptyState
                    message={
                        <FormattedMessage
                            id="notifications.allCaughtUp"
                            defaultMessage="All caught up! No new notifications."
                        />
                    }
                    height={{ xs: '100%', md: '400px' }}
                />
            )}
        </Box>
    );
});
